import * as api from '@codametrix/client-api';
import HttpMethods from '../enums/http-methods';
import * as conditionalUtils from '../core/amplify/utils/conditional-utilities';
import { isEmpty, set } from '../core/object-utils';
import {
  buildPatienttimelineDataAccessRequest,
  shouldFetchFromDataAccess
} from '../core/artifacts/utils';
const DATA_ACCESS_SERVICES_LOCATION = '/dataaccess/read/v1';

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'POST'
};

const getDataAccessPatientTimelinewithFilter = async <T>({
  caseRun,
  limit,
  page,
  timelineInfiniteLoad,
  messageTypeFilter,
  dateFilter,
  endDateFilter,
  ui
}) => {
  const queryURL = DATA_ACCESS_SERVICES_LOCATION;

  const filterFieldsMap: CMxAPI.CaseEntriesRequestObject = {
    mrn: caseRun.patient_identifier.number,
    limit
  };

  if (conditionalUtils.capabilityValue(timelineInfiniteLoad)) {
    filterFieldsMap.offset = limit * page - limit;
    if (!isEmpty(messageTypeFilter)) {
      const filterData = {
        and: [
          {
            message_type: messageTypeFilter?.map(item => item?.value) ?? []
          }
        ]
      };
      set(filterFieldsMap, 'filter', filterData);
    }

    if (!!dateFilter && !isEmpty(dateFilter)) {
      const andArray = filterFieldsMap?.filter?.and || []; // Use default empty array if bodyObject.filter.and is undefined
      let maxParam = {};
      if (!!endDateFilter && !isEmpty(endDateFilter)) {
        maxParam = {
          max: endDateFilter
        };
      }
      const filterData = {
        and: [
          ...andArray,
          {
            bedside_datetime: {
              min: dateFilter,
              ...maxParam
            }
          }
        ]
      };
      set(filterFieldsMap, 'filter', filterData);
    }
  }
  const patienttimelineRequestObject: CMxCommonApp.PatientTimelineDataAccessRequest = buildPatienttimelineDataAccessRequest(
    ui,
    'getPatientTimeline',
    'patienttimeline'
  );
  patienttimelineRequestObject.filterFieldsMap = filterFieldsMap;
  try {
    const response = await api.requestJSON<
      CMxAPI.PageableList<CMxAPI.CaseEntriesResponse>
    >(queryURL, {
      ...REQUEST_INIT_OPTIONS,
      body: JSON.stringify(patienttimelineRequestObject)
    });

    return response;
  } catch (error) {
    throw error;
  }
};

const getDataAccessPatientTimelineCaseWithoutFilter = async <T>({
  caseRun,
  limit,
  page,
  timelineInfiniteLoad,
  ui
}) => {
  const queryURL = DATA_ACCESS_SERVICES_LOCATION;

  const filterFieldsMap: CMxAPI.CaseEntriesRequestObject = {
    mrn: caseRun.patient_identifier.number,
    limit
  };

  if (conditionalUtils.capabilityValue(timelineInfiniteLoad)) {
    filterFieldsMap.offset = limit * page - limit;
  }

  const patienttimelineRequestObject: CMxCommonApp.PatientTimelineDataAccessRequest = buildPatienttimelineDataAccessRequest(
    ui,
    'getPatientTimeline',
    'patienttimeline'
  );
  patienttimelineRequestObject.filterFieldsMap = filterFieldsMap;

  try {
    const response = await api.requestJSON<
      CMxAPI.PageableList<CMxAPI.CaseEntriesResponse>
    >(queryURL, {
      ...REQUEST_INIT_OPTIONS,
      body: JSON.stringify(patienttimelineRequestObject)
    });

    return response;
  } catch (error) {
    throw error;
  }
};

const getDataAccessPatientMessageCountwithFilter = async <T>(
  timelineCountParams
) => {
  const { caseRun, dateFilter, endDateFilter, ui } = timelineCountParams;
  const queryURL = DATA_ACCESS_SERVICES_LOCATION;

  let filterFieldsMap: { [key: string]: string } = {
    patient_mrn: caseRun.patient_identifier.number
  };

  if (!!dateFilter) {
    filterFieldsMap = {
      ...filterFieldsMap,
      start_date: dateFilter
    };
  }
  if (!!endDateFilter) {
    filterFieldsMap = {
      ...filterFieldsMap,
      end_date: endDateFilter
    };
  }

  const patienttimelineRequestObject: CMxCommonApp.PatientTimelineCountDataAccessRequest = buildPatienttimelineDataAccessRequest(
    ui,
    'getPatientMessageCount',
    'patientmessagecount'
  );
  patienttimelineRequestObject.filterFieldsMap = filterFieldsMap;

  try {
    const response = await api.requestJSON<CMxAPI.MessageCounts[]>(queryURL, {
      ...REQUEST_INIT_OPTIONS,
      body: JSON.stringify(patienttimelineRequestObject)
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const getDataAccessPatientMessageCountWithoutFilter = async <T>(
  timelineCountParams
) => {
  const { caseRun, ui } = timelineCountParams;
  const queryURL = DATA_ACCESS_SERVICES_LOCATION;

  let filterFieldsMap: { [key: string]: string } = {
    patient_mrn: caseRun.patient_identifier.number
  };

  const patienttimelineRequestObject: CMxCommonApp.PatientTimelineCountDataAccessRequest = buildPatienttimelineDataAccessRequest(
    ui,
    'getPatientMessageCount',
    'patientmessagecount'
  );
  patienttimelineRequestObject.filterFieldsMap = filterFieldsMap;

  try {
    const response = await api.requestJSON<CMxAPI.MessageCounts[]>(queryURL, {
      ...REQUEST_INIT_OPTIONS,
      body: JSON.stringify(patienttimelineRequestObject)
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export {
  getDataAccessPatientTimelinewithFilter,
  getDataAccessPatientTimelineCaseWithoutFilter,
  getDataAccessPatientMessageCountwithFilter,
  getDataAccessPatientMessageCountWithoutFilter
};
